/**
 * Renders the sidebar nav from the 8-item menu config (buildMenuConfig).
 * Use this inside MainListItems in place of the previous list of NavIconButtons.
 */

import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Badge, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, Tooltip, Typography } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { i18n } from "../translate/i18n";

const NavIconButton = ({ icon, label, to, isActive, onClick, showBadge, badgeContent, classes, expanded, hasSubmenu }) => {
  const history = useHistory();
  const handleClick = (e) => {
    if (onClick) onClick(e);
    else if (to) history.push(to);
  };

  const iconContent = expanded && classes.iconButtonIconWrap ? (
    <span className={classes.iconButtonIconWrap}>
      {showBadge ? (
        <Badge
          badgeContent={badgeContent || "!"}
          overlap="rectangular"
          className={badgeContent ? classes.badgeContent : classes.badgeDot}
          variant={badgeContent ? "standard" : "dot"}
        >
          {icon}
        </Badge>
      ) : (
        icon
      )}
    </span>
  ) : (
    icon
  );

  const button = (
    <div
      className={`${classes.iconButton} ${isActive ? classes.iconButtonActive : ""} ${expanded ? classes.iconButtonExpanded || "" : ""}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      {iconContent}
      {expanded && classes.iconButtonLabel && (
        <span className={`${classes.iconButtonLabel} ${isActive && classes.iconButtonLabelActive ? classes.iconButtonLabelActive : ""}`}>
          {label}
        </span>
      )}
      {expanded && hasSubmenu && classes.iconButtonSubmenuIndicator && (
        <ChevronRightIcon className={classes.iconButtonSubmenuIndicator} fontSize="small" />
      )}
    </div>
  );

  const wrapped = !expanded && showBadge ? (
    <Badge
      badgeContent={badgeContent || "!"}
      overlap="rectangular"
      className={badgeContent ? classes.badgeContent : classes.badgeDot}
      variant={badgeContent ? "standard" : "dot"}
    >
      {button}
    </Badge>
  ) : (
    button
  );

  if (expanded) return wrapped;
  return (
    <Tooltip title={label} placement="right" arrow>
      {wrapped}
    </Tooltip>
  );
};

const PopoverMenu = ({ anchorEl, open, onClose, onMouseEnter, onMouseLeave, title, items, classes }) => {
  const location = useLocation();
  const history = useHistory();
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="right-start"
      transition
      style={{ zIndex: 1500 }}
      modifiers={{
        offset: { enabled: true, offset: "0, 8" },
        preventOverflow: { enabled: true, boundariesElement: "viewport" },
      }}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
          <Paper className={classes.popoverPaper} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <ClickAwayListener onClickAway={(e) => onClose(e)}>
              <div>
                {title && (
                  <div className={classes.popoverHeader}>
                    <Typography className={classes.popoverTitle}>{title}</Typography>
                  </div>
                )}
                <MenuList autoFocusItem={false}>
                  {items.map((item) => {
                    if (!item) return null;
                    const itemActive = location.pathname === item.to || (item.to === "/" && location.pathname === "/") || (item.to === "/tickets" && location.pathname.startsWith("/tickets"));
                    return (
                      <MenuItem
                        key={item.to}
                        className={`${classes.popoverItem} ${itemActive ? classes.popoverItemActive : ""}`}
                        onClick={() => {
                          history.push(item.to);
                          onClose();
                        }}
                      >
                        <div className={`${classes.popoverItemIcon} ${itemActive ? classes.popoverItemIconActive : ""}`}>
                          {item.icon}
                        </div>
                        <Typography className={`${classes.popoverItemText} ${itemActive ? classes.popoverItemTextActive : ""}`}>
                          {item.label}
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </div>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

export default function SidebarNavFromConfig({
  menuItems,
  classes,
  connectionWarning,
  openPopover,
  popoverAnchors,
  handlePopoverToggle,
  handlePopoverOpen,
  handlePopoverClose,
  handlePopoverKeepOpen,
  expanded = false,
}) {
  const location = useLocation();

  const isItemActive = (item) => {
    if (item.to) {
      if (item.to === "/tickets") return location.pathname === "/tickets" || location.pathname.startsWith("/tickets/");
      return location.pathname === item.to;
    }
    if (item.children) {
      return item.children.some(
        (c) =>
          location.pathname === c.to ||
          (c.to === "/" && location.pathname === "/") ||
          (c.to === "/tickets" && location.pathname.startsWith("/tickets"))
      );
    }
    return false;
  };

  const renderIcon = (IconComponent, size = 22) => {
    if (!IconComponent) return null;
    if (React.isValidElement(IconComponent)) return IconComponent;
    return React.createElement(IconComponent, { size });
  };

  return (
    <>
      {menuItems.map((item) => {
        const active = isItemActive(item);
        const label = i18n.t(item.labelKey);
        const isConnections = item.id === "connections";
        const showDividerBeforeAdmin = item.id === "administracao";

        return (
          <React.Fragment key={item.id}>
            {showDividerBeforeAdmin && <div className={expanded ? classes.dividerExpanded : classes.divider} style={{ margin: "8px 0" }} />}
            {item.to ? (
              expanded ? (
                <NavIconButton
                  icon={renderIcon(item.icon)}
                  label={label}
                  to={item.to}
                  isActive={active}
                  showBadge={isConnections && connectionWarning}
                  classes={classes}
                  expanded={expanded}
                  hasSubmenu={false}
                />
              ) : (
                <div className={classes.navItemCollapsed}>
                  <NavIconButton
                    icon={renderIcon(item.icon)}
                    label={label}
                    to={item.to}
                    isActive={active}
                    showBadge={isConnections && connectionWarning}
                    classes={classes}
                    expanded={expanded}
                    hasSubmenu={false}
                  />
                </div>
              )
            ) : (
              <div
                className={!expanded ? classes.navItemCollapsed : undefined}
                style={{ width: "100%" }}
                data-nav-submenu-trigger
              >
                <NavIconButton
                  icon={renderIcon(item.icon)}
                  label={label}
                  isActive={active}
                  classes={classes}
                  expanded={expanded}
                  hasSubmenu={true}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePopoverToggle(item.id, e);
                  }}
                />
                <PopoverMenu
                  anchorEl={popoverAnchors.current[item.id]}
                  open={openPopover === item.id}
                  onClose={(event) => handlePopoverClose(event)}
                  onMouseEnter={handlePopoverKeepOpen}
                  onMouseLeave={handlePopoverClose}
                  title={label}
                  items={item.children.map((c) => ({
                    ...c,
                    icon: !c.icon ? null : React.isValidElement(c.icon) ? c.icon : React.createElement(c.icon, { size: 18 }),
                  }))}
                  classes={classes}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
